import { useCallback, useEffect, useMemo, useState } from "react";
import { FieldValues, UseFormReturn } from "react-hook-form";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { z } from "zod";
import { SP_BREAKPOINT } from "../../../constants/app";
import { dataSelectors } from "../../../ducks/data";
import { fileSelectors } from "../../../ducks/file";
import { Button } from "../../atoms/Button";
import { DatePicker } from "../../atoms/DatePicker";
import { Input } from "../../atoms/Input";
import { Select } from "../../atoms/Select";
import { SearchIcon } from "../../atoms/icons/SearchIcon";
import FormTemplate from "../../templates/FormTemplate";

type Props = {
  onReset: () => void;
  handleSubmit: (payload: {
    fileName?: string;
    userId?: string;
    menuId?: number;
    templateId?: number;
    fromDate?: string;
    toDate?: string;
    status?: number;
  }) => void;
  onMethods?: (methods: UseFormReturn<FieldValues, unknown, FieldValues>) => void;
};

export const FileUploadFilterForm: React.FC<Props> = ({
  onReset,
  handleSubmit,
  onMethods,
}) => {
  const schema = useMemo(() => {
    return z.object({
      fileName: z.string().min(0).optional(),
      userId: z.string().min(0).optional(),
      menuId: z.number().min(0).optional(),
      templateId: z.number().min(0).optional(),
      fromDate: z.string().min(0).optional(),
      toDate: z.string().min(0).optional(),
      status: z.number().min(0).optional(),
    });
  }, []);

  const [methods, setMethods] = useState<UseFormReturn<
    FieldValues,
    unknown,
    FieldValues
  > | null>(null);

  useEffect(() => {
    if (methods) {
      onMethods?.(methods);
    }
  }, [methods]);

  const handleReset = useCallback(() => {
    methods?.reset();
    onReset();
  }, [methods]);

  const users = useSelector(fileSelectors.users);
  const templates = useSelector(fileSelectors.templates);
  const menus = useSelector(dataSelectors.menus);
  const registrationResults = useSelector(dataSelectors.registrationResults);

  return (
    <FormTemplate
      schema={schema}
      setMethods={setMethods}
      defaultValues={{
        fileName: undefined,
        userId: undefined,
        menuId: undefined,
        templateId: undefined,
        fromDate: undefined,
        toDate: undefined,
        status: undefined,
      }}
      onSubmit={({
        fileName,
        userId,
        menuId,
        templateId,
        fromDate,
        toDate,
        status,
      }) =>
        handleSubmit({
          fileName,
          userId,
          menuId,
          templateId,
          fromDate,
          toDate,
          status,
        })
      }
    >
      <StyledFilterContent>
        <StyledInputRowContainer>
          <Input
            name="fileName"
            label="ファイル名"
            fullWidth
            autoComplete="file-name"
          />
          <Select
            name="userId"
            label="実施ユーザー"
            placeholder="選択してください"
            options={users.map((user) => ({
              id: user.id,
              name: user.name,
              value: user.id,
            }))}
            onChange={() => {}}
            width="100%"
          />
          <Select
            name="menuId"
            label="メニュー名"
            placeholder="選択してください"
            options={menus.map((menu) => ({
              id: menu.id,
              name: menu.name,
              value: menu.id,
            }))}
            onChange={() => {}}
            width="100%"
            hasSearchInput={false}
          />
          <Select
            name="templateId"
            label="テンプレート"
            placeholder="選択してください"
            options={templates.map((template) => ({
              id: template.id,
              name: template.name,
              value: template.id,
            }))}
            onChange={() => {}}
            width="100%"
          />
        </StyledInputRowContainer>

        <StyledInputRowContainer $withButton>
          <StyledColumnContainer>
            <StyledDateRangeContainer>
              <DatePicker
                name="fromDate"
                label="実施日時"
                defaultCalendarValue={new Date()}
              />
              <span>〜</span>
              <DatePicker name="toDate" defaultCalendarValue={new Date()} />
            </StyledDateRangeContainer>
            <Select
              name="status"
              label="登録結果"
              placeholder="選択してください"
              options={registrationResults.map((result) => ({
                id: result.id,
                name: result.name,
                value: result.id,
              }))}
              onChange={() => {}}
              width="100%"
              hasSearchInput={false}
            />
          </StyledColumnContainer>
          <StyledButtonContainer>
            <Button
              text="リセット"
              variant="dark"
              type="button"
              onClick={handleReset}
              fullWidth
            />
            <Button
              text="検索"
              variant="border"
              type="submit"
              icon={SearchIcon}
              fullWidth
            />
          </StyledButtonContainer>
        </StyledInputRowContainer>
      </StyledFilterContent>
    </FormTemplate>
  );
};

const StyledFilterContent = styled.div`
  width: 100%;
  padding: 20px 32px 28px 32px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 14px;
  margin-bottom: 32px;
`;

const StyledInputRowContainer = styled.div<{ $withButton?: boolean }>`
  @media (max-width: ${SP_BREAKPOINT}) {
    flex-direction: column;
    justify-content: flex-end;
  }

  width: 100%;
  display: flex;
  align-items: baseline;
  ${({ $withButton }) =>
    $withButton && "justify-content: space-between; flex-wrap: wrap;"}
  gap: 24px;
`;

const StyledColumnContainer = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 24px;
  width: 65%;

  @media (max-width: ${SP_BREAKPOINT}) {
    flex-direction: column;
    align-items: flex-start;
    width: 100%;
    max-width: 100%;
  }
`;
const StyledDateRangeContainer = styled.div`
  display: flex;
  align-items: last baseline;
  gap: 14px;
  width: 200%;
  @media (max-width: ${SP_BREAKPOINT}) {
    flex-wrap: wrap;
    gap: 4px;
    justify-content: start;
    width: 100%;
  }
`;

const StyledButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  max-width: 100%;
  min-width: 24%;
  @media (max-width: ${SP_BREAKPOINT}) {
    width: 100%;
    max-width: 100%;
  }
`;
